import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AddClient from '../components/AddClient';
import ClientDetail from '../components/ClientDetail';
import Sidebar from '../components/Sidebar'; // Import ajouté

// Icônes SVG
const UserIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const EditIcon = () => (
    <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
);

const DeleteIcon = () => (
    <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);

const ViewIcon = () => (
    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
);

const ClientPage = () => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [showAddClient, setShowAddClient] = useState(false);
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editingClient, setEditingClient] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [viewingClient, setViewingClient] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [filters, setFilters] = useState({
        client_type: 'all', // 'all', 'particulier', 'entreprise'
    });


    // Récupérer les clients depuis l'API
    useEffect(() => {
        const fetchClientsData = async () => {
            try {
                setLoading(true);
                setError(null);
                const response = await fetch('https://krili-loto.com/api/clients');

                if (!response.ok) {
                    throw new Error(`Erreur HTTP! statut: ${response.status}`);
                }

                const data = await response.json();

                if (!Array.isArray(data)) {
                    throw new Error('Format de données invalide: tableau attendu');
                }

                setClients(data);
            } catch (error) {
                console.error("Erreur lors de la récupération des clients:", error);
                setError(error.message);
                setClients([]);
            } finally {
                setLoading(false);
            }
        };

        fetchClientsData();
    }, []);

    const fetchClients = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetch('https://krili-loto.com/api/clients');

            if (!response.ok) {
                throw new Error(`Erreur HTTP! statut: ${response.status}`);
            }

            const data = await response.json();
            if (!Array.isArray(data)) {
                throw new Error('Format de données invalide: tableau attendu');
            }

            setClients(data);
        } catch (error) {
            console.error("Erreur lors de la récupération des clients:", error);
            setError(error.message);
            setClients([]);
        } finally {
            setLoading(false);
        }
    };


    const filteredClients = clients.filter(client => {
        // Filtre par terme de recherche
        const matchesSearch =
            client.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
            client.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (client.telephone && client.telephone.includes(searchTerm)) ||
            (client.numero_permis && client.numero_permis.includes(searchTerm));

        // Filtre par type de client
        const matchesType =
            filters.client_type === 'all' ||
            client.client_type === filters.client_type;

        return matchesSearch && matchesType;
    });

    const handleClientAdded = () => {
        fetchClients(); // <-- recharge les données
        setShowAddClient(false);
    };

    const handleClientUpdated = () => {
        fetchClients(); // <-- recharge les données mises à jour
        setEditingClient(null);
    };

    const handleDeleteClient = async (id) => {
        try {
            const response = await fetch(`https://krili-loto.com/api/clients/${id}`, {
                method: 'DELETE'
            });

            if (!response.ok) {
                throw new Error(t('error_delete_client'));
            }

            // Au lieu de filtrer manuellement, on re‑récupère toute la liste
            await fetchClients();
            setDeleteConfirm(null);
        } catch (error) {
            console.error("Erreur lors de la suppression du client:", error);
            setError(error.message);
        }
    };


    return (
        <Sidebar>

            <div
                className={`min-h-screen bg-gradient-to-br from-blue-50 to-blue-100 p-6 ${showAddClient || editingClient ? 'overflow-hidden' : ''} ${isRtl ? 'rtl' : 'ltr'}`}
                dir={isRtl ? 'rtl' : 'ltr'}
            >
                <div className={`max-w-7xl mx-auto relative ${isRtl ? 'text-right' : 'text-left'}`}>
                    {/* Main content with blur effect when modal is open */}
                    <div className={`transition-all ${showAddClient || editingClient ? 'blur-sm' : ''}`}>
                        {/* Nouvelle section supérieure pour recherche et filtres - Design premium */}
                        <div className="bg-white rounded-2xl shadow-2xl p-6 mb-8 border border-gray-100">
                            <div className={`flex flex-col md:flex-row justify-between items-center gap-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                {/* Barre de recherche - Design luxe */}
                                <div className="relative flex-grow max-w-2xl w-full">
                                    <div className={`absolute inset-y-0 ${isRtl ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                                        <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder={t('search_clients_placeholder')}
                                        className={`block w-full ${isRtl ? 'pr-12 pl-4' : 'pl-12 pr-4'} py-3 border-2 border-blue-100 rounded-xl bg-white shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 text-gray-700 placeholder-blue-300 transition-all duration-300 hover:shadow-md ${isRtl ? 'text-right' : 'text-left'}`}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            className={`absolute inset-y-0 ${isRtl ? 'left-0 pl-4' : 'right-0 pr-4'} flex items-center text-blue-400 hover:text-blue-600 transition-colors`}
                                        >
                                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    )}
                                </div>

                                {/* Filtres - Design élégant */}
                                <div className={`flex items-center ${isRtl ? 'space-x-reverse space-x-4' : 'space-x-4'}`}>
                                    <div className="relative group">
                                        <label
                                            className={`absolute -top-2 ${isRtl ? 'right-3' : 'left-3'} bg-white px-1 text-xs font-medium text-blue-600`}>
                                            {t('type_col')}
                                        </label>
                                        <select
                                            className={`appearance-none bg-white border-2 border-blue-100 rounded-xl ${isRtl ? 'pr-4 pl-10' : 'pl-4 pr-10'} py-2.5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 shadow-sm transition-all duration-200 hover:shadow-md ${isRtl ? 'text-right' : 'text-left'}`}
                                            value={filters.client_type}
                                            onChange={(e) => setFilters({ ...filters, client_type: e.target.value })}
                                        >
                                            <option value="all">{t('all_types')}</option>
                                            <option value="particulier">{t('particulier')}</option>
                                            <option value="entreprise">{t('entreprise')}</option>
                                        </select>
                                        <div
                                            className={`absolute inset-y-0 ${isRtl ? 'left-0 pl-3' : 'right-0 pr-3'} flex items-center pointer-events-none`}>
                                            <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                    d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>

                                    <button
                                        onClick={() => {
                                            setEditingClient(null);
                                            setShowAddClient(true);
                                        }}
                                        className={`flex items-center ${isRtl ? 'space-x-reverse space-x-2' : 'space-x-2'} bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-3 rounded-xl shadow-xl hover:shadow-2xl transition-all transform hover:scale-105 active:scale-95 group`}
                                    >
                                        <svg className="w-5 h-5 transition-transform group-hover:rotate-90" fill="none"
                                            stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M12 4v16m8-8H4" />
                                        </svg>
                                        <span className="font-medium">{t('new_client')}</span>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Contenu principal */}
                        <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                            <div className="p-6">
                                {/* En-tête du tableau */}
                                <div className={`flex justify-between items-center mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <h2 className={`text-2xl font-semibold text-gray-800 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <svg className={`w-6 h-6 text-blue-500 ${isRtl ? 'ml-2' : 'mr-2'}`} fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                        {t('client_list')}
                                    </h2>
                                    <p className="text-sm text-gray-500">
                                        {filteredClients.length} {filteredClients.length === 1 ? t('client_found') : t('clients_found')}
                                    </p>
                                </div>
                                {loading ? (
                                    <div className="flex justify-center items-center h-64">
                                        <div
                                            className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                                    </div>
                                ) : filteredClients.length === 0 ? (
                                    <div className="text-center py-12">
                                        <svg className="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                                                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <h3 className="mt-4 text-xl font-medium text-gray-700">{t('no_client_found')}</h3>
                                        <p className="mt-2 text-gray-500">{t('add_first_client_desc')}</p>
                                        <div className="mt-6">
                                            <button
                                                onClick={() => setShowAddClient(true)}
                                                className="inline-flex items-center px-6 py-3 border border-transparent shadow-sm text-sm font-medium rounded-xl text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transform hover:scale-105 transition-transform"
                                            >
                                                <svg className={`${isRtl ? '-mr-1 ml-2' : '-ml-1 mr-2'} h-5 w-5`} fill="none" stroke="currentColor"
                                                    viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                        d="M12 4v16m8-8H4" />
                                                </svg>
                                                {t('add_client_btn')}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <div className="hidden md:block overflow-x-auto">
                                            <table className={`min-w-full divide-y divide-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                                                <thead className="bg-gray-50">
                                                    <tr className={isRtl ? 'flex-row-reverse' : ''}>
                                                        <th scope="col"
                                                            className={`px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>
                                                            {t('client_col')}
                                                        </th>
                                                        <th scope="col"
                                                            className={`px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>
                                                            {t('contact_col')}
                                                        </th>
                                                        <th scope="col"
                                                            className={`px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>
                                                            {t('type_col')}
                                                        </th>
                                                        <th scope="col"
                                                            className={`px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-right' : 'text-left'}`}>
                                                            {t('license_col')}
                                                        </th>
                                                        <th scope="col"
                                                            className={`px-6 py-4 text-xs font-medium text-gray-500 uppercase tracking-wider ${isRtl ? 'text-left' : 'text-right'}`}>
                                                            {t('actions_col')}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {filteredClients.map((client) => (
                                                        <tr
                                                            key={client._id || client.id}
                                                            className="text-gray-700 hover:bg-blue-50 transition-colors duration-200"
                                                        >
                                                            <td className="px-6 py-5 whitespace-nowrap">
                                                                <div className={`flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}>
                                                                    <div
                                                                        className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                                        <svg className="h-6 w-6 text-blue-600" fill="none"
                                                                            stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round"
                                                                                strokeWidth={1.5}
                                                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                                        </svg>
                                                                    </div>
                                                                    <div className={isRtl ? 'mr-4' : 'ml-4'}>
                                                                        <div
                                                                            className="text-sm font-medium text-blue-800">{client.prenom} {client.nom}</div>
                                                                        <div
                                                                            className="text-sm text-gray-500">{client.adresse}</div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td className="px-6 py-5 whitespace-nowrap">
                                                                <div className="text-sm text-blue-800">{client.email}</div>
                                                                <div className="text-sm text-gray-500">{client.telephone}</div>
                                                            </td>
                                                            <td className="px-6 py-5 whitespace-nowrap">
                                                                <span
                                                                    className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${client.client_type === 'particulier' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`}>
                                                                    {client.client_type === 'particulier' ? t('particulier') : t('entreprise')}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-5 whitespace-nowrap text-sm text-gray-500">
                                                                {client.numero_permis}
                                                            </td>
                                                            <td className={`px-6 py-5 whitespace-nowrap text-sm font-medium ${isRtl ? 'text-left' : 'text-right'} space-x-2 ${isRtl ? 'space-x-reverse' : ''}`}>
                                                                <button
                                                                    onClick={() => setViewingClient(client)}
                                                                    className="text-blue-600 hover:text-blue-800 p-2 rounded-lg bg-blue-100 hover:bg-blue-200 transition-colors transform hover:scale-110"
                                                                >
                                                                    <ViewIcon />
                                                                </button>
                                                                <button
                                                                    onClick={() => setEditingClient(client)}
                                                                    className="text-orange-600 bg-orange-100 hover:text-orange-800 hover:bg-orange-200 p-2 rounded-lg transition-colors transform hover:scale-110"
                                                                >
                                                                    <EditIcon />
                                                                </button>
                                                                <button
                                                                    onClick={() => setDeleteConfirm(client.id)}
                                                                    className="text-red-600 hover:text-red-800 p-2 rounded-lg bg-red-100 hover:bg-red-200 transition-colors transform hover:scale-110"
                                                                >
                                                                    <DeleteIcon />
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="block md:hidden space-y-4 p-4">
                                            {filteredClients.map((client) => (
                                                <div key={client._id || client.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
                                                    <div className="flex items-center gap-3">
                                                        <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                                                            <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <div className="text-sm font-semibold text-blue-800">{client.prenom} {client.nom}</div>
                                                            <div className="text-xs text-gray-500">{client.adresse}</div>
                                                        </div>
                                                    </div>
                                                    <div className="border-t border-gray-100 pt-2 space-y-1 text-sm">
                                                        <p><span className="text-gray-500">{t('email')}:</span> {client.email}</p>
                                                        <p><span className="text-gray-500">{t('phone')}:</span> {client.telephone}</p>
                                                        <p><span className="text-gray-500">{t('license_col')}:</span> {client.numero_permis}</p>
                                                    </div>
                                                    <div className="border-t border-gray-100 pt-2 flex justify-between items-center">
                                                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${client.client_type === 'particulier' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`}>
                                                            {client.client_type === 'particulier' ? t('particulier') : t('entreprise')}
                                                        </span>
                                                        <div className="flex space-x-2">
                                                            <button
                                                                onClick={() => setViewingClient(client)}
                                                                className="text-blue-600 p-2 rounded-lg bg-blue-50"
                                                            >
                                                                <ViewIcon />
                                                            </button>
                                                            <button
                                                                onClick={() => setEditingClient(client)}
                                                                className="text-orange-600 p-2 rounded-lg bg-orange-50"
                                                            >
                                                                <EditIcon />
                                                            </button>
                                                            <button
                                                                onClick={() => setDeleteConfirm(client.id)}
                                                                className="text-red-600 p-2 rounded-lg bg-red-50"
                                                            >
                                                                <DeleteIcon />
                                                            </button>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Add Client Modal */}
                    {(showAddClient || editingClient) && (
                        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                            <div
                                className="absolute inset-0 bg-black bg-opacity-30"
                                onClick={() => {
                                    setShowAddClient(false);
                                    setEditingClient(null);
                                }}
                            ></div>

                            <div className="relative z-10 w-full max-w-6xl rounded-2xl">
                                <AddClient
                                    onClientAdded={handleClientAdded}
                                    onClientUpdated={handleClientUpdated}
                                    editingClient={editingClient}
                                    onCancel={() => {
                                        setShowAddClient(false);
                                        setEditingClient(null);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Modal de détail client */}
                    {viewingClient && (
                        <ClientDetail
                            client={viewingClient}
                            onClose={() => setViewingClient(null)}
                            onEdit={(c) => { setEditingClient(c); setViewingClient(null); }}
                            onDelete={(id) => { setDeleteConfirm(id); setViewingClient(null); }}
                        />
                    )}

                    {/* Modal de confirmation de suppression */}
                    {deleteConfirm && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
                            <div
                                className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-md border border-gray-300 animate-fadeIn"
                                dir={isRtl ? 'rtl' : 'ltr'}
                            >
                                <div className="p-6">
                                    <div className={`flex items-start ${isRtl ? 'flex-row-reverse' : ''}`}>
                                        <div
                                            className={`flex-shrink-0 h-10 w-10 rounded-full bg-red-100 flex items-center justify-center ${isRtl ? 'ml-4' : ''}`}>
                                            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                        </div>
                                        <div className={isRtl ? 'text-right' : 'text-left'}>
                                            <h3 className="text-lg font-medium text-gray-900">{t('delete_client_title')}</h3>
                                            <div className="mt-2">
                                                <p className="text-sm text-gray-500">
                                                    {t('delete_client_confirm')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className={`bg-gray-50 p-4 border-t border-gray-200 flex justify-end gap-3 ${isRtl ? 'flex-row-reverse' : ''}`}>
                                    <button
                                        onClick={() => setDeleteConfirm(null)}
                                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClient(deleteConfirm)}
                                        className={`px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center ${isRtl ? 'space-x-reverse space-x-2' : 'space-x-2'}`}
                                    >
                                        <DeleteIcon />
                                        <span>{t('delete')}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Sidebar>

    );
};

export default ClientPage;
